import { CookieService } from "angular2-cookie/core";
import * as lodash from "lodash";
import { Headers, Http, RequestOptionsArgs, Response } from "@angular/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
    APP_AUTH_ADMIN,
    APP_AUTH_READ_ONLY,
    APP_AUTH_USER,
    APPLICATION_SCOPES,
    ITCIApplicationAuth,
    ITCIUserCap,
    ITCIUserInfo,
    ITCIUserProfile,
    USER_ROLES,
    SERVICE_URLS,
    ITCIUserLogout,
    BASE_URLS,
    BASE_CONSTANTS, APPLICATION_SCOPE_TYPE
} from "@tci/common";

@Injectable()
export class NgUserService {

    private static instance: NgUserService = null;

    private readonly adminAuth: ITCIApplicationAuth = APP_AUTH_ADMIN;
    private readonly userAuth: ITCIApplicationAuth = APP_AUTH_USER;
    private readonly readonlyAuth: ITCIApplicationAuth = APP_AUTH_READ_ONLY;

    private userInfo$: Observable<ITCIUserInfo> = null;
    private userInfo: ITCIUserInfo = null;
    private userProfile: ITCIUserProfile = null;
    private initialized = false;
    private scopes = APPLICATION_SCOPES;

    public static getInstance(cookieService: CookieService, http: Http): NgUserService {
        if (!NgUserService.instance) {
            NgUserService.instance = new NgUserService(cookieService, http);
        }
        return NgUserService.instance;
    }

    constructor(
        private cookieService: CookieService,
        private http: Http
    ) { }

    public getUserInfo(update: boolean = false): Observable<ITCIUserInfo> {
        if (this.initialized && this.userInfo && !update) {
            return Observable.of(this.userInfo);
        } else {
            const options: RequestOptionsArgs = {
                headers: new Headers({
                    "x-atmosphere-token": this.cookieService.get("AtmosphereWIFlogoSession")
                })
            };

            if (!this.userInfo$ || update) {
                this.initialized = false;
                this.userInfo$ = this.http.get(SERVICE_URLS.WHOAMI, options)
                    .map((res: Response) => {
                        this.initialized = true;
                        return this.userInfo = res && <ITCIUserInfo>res.json() || null;
                    })
                    .catch(error => {
                        console.log("Error occurred for NgUserService#getUser(): ", error);
                        return Observable.of(null);
                    })
                    .finally(() => {
                        console.log("The user info is ", this.userInfo);
                        delete this.userInfo$;
                    })
                    .share();
            }

            return this.userInfo$;
        }
    }

    public getUserProfile(update: boolean): Observable<ITCIUserProfile> {

        if (this.userProfile && !update) {
            return Observable.of(this.userProfile);
        } else {
            return Observable.create(observer => {
                this
                    .http
                    .get(SERVICE_URLS.USER_PROFILE)
                    .subscribe(
                        res => {
                            observer.next(this.userProfile = res && <ITCIUserProfile>res.json() || null);
                            observer.complete();
                        },
                        res => {
                            console.log("Error occurred for NgUserService#getUserProfile(): ", res);
                            observer.next(null);
                            observer.complete();
                        }
                    );
            });
        }
    }

    public getUserCap(): Observable<ITCIUserCap> {
        const url = `${SERVICE_URLS.USERCAP}`;

        return Observable.create(observer => {
            this
                .http
                .get(url)
                .subscribe(
                    res => {
                        observer.next(res && <ITCIUserCap>res.json() || null);
                        observer.complete();
                    },
                    res => {
                        console.log("Error occurred for NgUserService#getUserCap(): ", res);
                        observer.next(null);
                        observer.complete();
                    }
                );
        });
    }

    /**
     * Perform logout orchestration
     */
    public logout(): Observable<any> {
        const url = `${SERVICE_URLS.USER_LOGOUT}`;

        return Observable.create(observer => {
            this.http.get(url)
                .subscribe(
                    res => {
                        this.userProfile = null;
                        this.removeUser();
                        observer.next(res && <ITCIUserLogout>res.json() || null);
                        observer.complete();
                    },
                    res => {
                        console.log("Error occurred for NgUserService#logout(): ", res);
                        observer.error(res);
                        observer.complete();
                    }
                );
        });
    }

    public deleteCookie(cookieName: string) {
        const url = `${BASE_URLS.WEBSERVER}${BASE_CONSTANTS.DELETE_COOKIE}/${cookieName}`;
        return this.http.get(url).subscribe();
    }

    public isAuthenticated(): Observable<boolean> {
        return this.getUserInfo()
            .map((userInfo: ITCIUserInfo) => {
                return userInfo !== null;
            })
            .catch(() => {
                return Observable.of(false);
            });
    }

    public removeUser(): void {
        this.userInfo = null;
    }

    public getAuthForFunction(scope: APPLICATION_SCOPE_TYPE | string): Observable<ITCIApplicationAuth> {
        if (this.scopes.hasOwnProperty(scope)) {
            return this.getCurrentUserRole()
                .map((role: string) => {
                    if (role === USER_ROLES.ADM) {
                        return this.adminAuth;
                    } else if (role === USER_ROLES.USR) {
                        return this.userAuth;
                    } else {
                        return this.readonlyAuth;
                    }
                });
        } else {
            return Observable.of(this.readonlyAuth);
        }
    }

    public dontShowAgain(username: string): Observable<any> {
        const url = `${BASE_URLS.WEBSERVER}/${BASE_CONSTANTS.DOMAIN_SERVER}/users/${username}/${BASE_CONSTANTS.WELCOME_SCREEN}`;

        return Observable.create(observer => {
            this.http.put(url, null)
                .subscribe(
                    res => {
                        observer.next(res.json() || null);
                        observer.complete();
                    },
                    res => {
                        console.log("Error occurred for NgUserService#dontShowAgain(): ", res);
                        observer.error(res);
                        observer.complete();
                    }
                );
        });
    }

    private getCurrentUserRole(): Observable<string> {
        return this.getJWT()
            .map((userInfo: ITCIUserInfo) => {
                // TODO: confirm whether it's "roles" or "rolX"
                if (userInfo && userInfo.rolX) {
                    return userInfo.rolX[0];
                } else {
                    return USER_ROLES.USR;
                }
            });
    }

    private getJWT(): Observable<ITCIUserInfo> {
        if (!lodash.isEmpty(this.userInfo)) {
            return Observable.of(this.userInfo);
        } else {
            return this.getUserInfo()
                .map((userInfo: ITCIUserInfo) => {
                    return userInfo;
                })
                .catch(error => {
                    console.log("Error getting user info while getting JWT, ", error);
                    return Observable.of(null);
                });
        }
    }
}

// if (typeof angular !== "undefined") {
//     angular
//         .module("main")
//         .factory("tciUser", downgradeInjectable(NgUserService) as any);
// }
